import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { version } from '../package.json';
import { WebRelease } from '../types/webRelease';
import { withTimeout } from '../utils/asyncTimeout';
import { WebUpdateError } from '../utils/webUpdateError';

const buildId = process.env.EXPO_PUBLIC_WEB_BUILD_ID || 'development';
const channel = process.env.EXPO_PUBLIC_WEB_RELEASE_CHANNEL || 'local';
let registrationPromise: Promise<ServiceWorkerRegistration> | undefined;
let registrationBuildId: string | undefined;
const installationFailures = new Map<string, string>();

function installationFailure(event: MessageEvent): void {
  if (
    event.data?.type !== 'APP_UPDATE_FAILED' ||
    !validRelease(event.data.release)
  )
    return;
  const message = String(event.data.error || 'Worker installation failed');
  if (installationFailures.get(event.data.release.buildId) === message) return;
  installationFailures.set(event.data.release.buildId, message);
  console.error('[web-update] Worker installation failed', {
    release: event.data.release,
    error: message,
  });
}

export function getAppWorkerRegistration(targetBuildId = buildId): Promise<ServiceWorkerRegistration> {
  if (!registrationPromise || registrationBuildId !== targetBuildId) {
    registrationBuildId = targetBuildId;
    registrationPromise = navigator.serviceWorker
      .register(`/firebase-messaging-sw.js?build=${encodeURIComponent(targetBuildId)}`, {
        scope: '/',
        updateViaCache: 'none',
      })
      .catch((error) => {
        registrationPromise = undefined;
        throw error;
      });
  }
  return registrationPromise;
}
function validRelease(value: unknown): value is WebRelease {
  const release = value as WebRelease | null;
  return (
    !!release &&
    typeof release.version === 'string' &&
    typeof release.buildId === 'string' &&
    release.channel === channel
  );
}
function different(release: WebRelease) {
  return release.buildId !== buildId || release.version !== version;
}
function workerRelease(worker: ServiceWorker): Promise<WebRelease | null> {
  return new Promise((resolve) => {
    const ports = new MessageChannel();
    const timeout = window.setTimeout(() => {
      ports.port1.close();
      resolve(null);
    }, 2000);
    ports.port1.onmessage = (event) => {
      window.clearTimeout(timeout);
      ports.port1.close();
      resolve(validRelease(event.data) ? event.data : null);
    };
    try {
      worker.postMessage({ type: 'GET_RELEASE' }, [ports.port2]);
    } catch {
      window.clearTimeout(timeout);
      ports.port1.close();
      ports.port2.close();
      resolve(null);
    }
  });
}

export function watchWebUpdates(
  onAvailable: (release: WebRelease | null) => void,
): () => void {
  if (__DEV__ || typeof window === 'undefined' || !window.isSecureContext)
    return () => {};
  let active = true;
  let checking = false;
  const report = (release: unknown) => {
    if (active)
      onAvailable(validRelease(release) && different(release) ? release : null);
  };
  const supported = 'serviceWorker' in navigator;
  const onMessage = (event: MessageEvent) => {
    installationFailure(event);
    if (event.data?.type === 'GET_CLIENT_BUILD')
      event.ports[0]?.postMessage({ buildId });
  };
  const ready = () =>
    navigator.serviceWorker.controller?.postMessage({
      type: 'CLIENT_READY',
      buildId,
    });
  const refresh = async () => {
    if (!active || checking || document.visibilityState === 'hidden') return;
    checking = true;
    try {
      try {
        // Firestore is a signal to check this origin, not proof that its alias
        // serves the announced build. Also clear banners after rollbacks.
        report(await hostedRelease());
      } catch {
        report(null);
      }
      if (supported) {
        const registration = await withTimeout(
          getAppWorkerRegistration(),
          20000,
          'Worker registration timed out',
        );
        await withTimeout(
          registration.update(),
          20000,
          'Worker update check timed out',
        );
      }
    } catch {
      /* Offline/export unavailable: keep the running app intact. */
    } finally {
      checking = false;
    }
  };
  if (supported) {
    navigator.serviceWorker.addEventListener('message', onMessage);
    navigator.serviceWorker.addEventListener('controllerchange', ready);
    getAppWorkerRegistration()
      .then((registration) => {
        if (!active) return;
        registration.addEventListener('updatefound', installed);
        ready();
        installed();
      })
      .catch(() => {});
  }
  function installed() {
    getAppWorkerRegistration()
      .then((registration) => {
        const worker = registration.installing;
        if (!worker) {
          void refresh();
          return;
        }
        worker.addEventListener('statechange', () => {
          if (active && worker.state === 'installed') void refresh();
        });
      })
      .catch(() => {});
  }
  const unsubscribe = onSnapshot(
    doc(db, 'app_releases', `web_${channel}`),
    (snapshot) => {
      if (
        !snapshot.metadata.fromCache &&
        snapshot.exists() &&
        validRelease(snapshot.data())
      ) {
        void refresh();
      }
    },
    () => {
      /* A missing deployment/configuration must not block login. */
    },
  );
  const timer = window.setInterval(() => {
    void refresh();
  }, 120000);
  window.addEventListener('online', refresh);
  document.addEventListener('visibilitychange', refresh);
  void refresh();
  return () => {
    active = false;
    unsubscribe();
    window.clearInterval(timer);
    window.removeEventListener('online', refresh);
    document.removeEventListener('visibilitychange', refresh);
    if (supported) {
      navigator.serviceWorker.removeEventListener('message', onMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', ready);
      registrationPromise
        ?.then((registration) =>
          registration.removeEventListener('updatefound', installed),
        )
        .catch(() => {});
    }
  };
}

async function hostedRelease(): Promise<WebRelease> {
  const abort = new AbortController();
  const timeout = window.setTimeout(() => abort.abort(), 15000);
  try {
    const response = await fetch(`/release.json?check=${Date.now()}`, {
      cache: 'no-store',
      signal: abort.signal,
    });
    if (!response.ok)
      throw new WebUpdateError(
        'unavailable',
        'Release metadata HTTP ' + response.status,
      );
    const deployed: unknown = await response.json();
    if (!validRelease(deployed))
      throw new WebUpdateError(
        'unavailable',
        'Invalid release metadata for this channel',
      );
    return deployed;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function applyWebUpdate(release: WebRelease): Promise<void> {
  let registration: ServiceWorkerRegistration | undefined;
  let stage = 'release';
  installationFailures.delete(release.buildId);
  const failures = (event: MessageEvent) => installationFailure(event);
  navigator.serviceWorker?.addEventListener('message', failures);
  try {
    // A visible, uncached request also distinguishes a stale announcement from a
    // worker installation problem before polling browser-internal worker state.
    const deployed = await hostedRelease();
    if (
      !validRelease(deployed) ||
      deployed.buildId !== release.buildId ||
      deployed.version !== release.version
    )
      throw new WebUpdateError(
        'unavailable',
        'Announced release differs from hosting: ' +
          JSON.stringify({ expected: release, deployed }),
      );
    stage = 'registration';
    await prepareWebUpdate(
      release,
      async () => {
        registration = await withTimeout(
          getAppWorkerRegistration(release.buildId),
          20000,
          'Worker registration timed out',
        );
        return registration;
      },
      (value) => {
        stage = value;
      },
    );
  } catch (error) {
    console.error('[web-update] Update failed', {
      stage,
      release,
      currentBuildId: buildId,
      installing: registration?.installing?.state ?? null,
      waiting: registration?.waiting?.state ?? null,
      active: registration?.active?.state ?? null,
      installationError: installationFailures.get(release.buildId) ?? null,
      error,
    });
    throw error instanceof WebUpdateError
      ? error
      : new WebUpdateError('download', String(error));
  } finally {
    navigator.serviceWorker?.removeEventListener('message', failures);
  }
}

async function prepareWebUpdate(
  release: WebRelease,
  register: () => Promise<ServiceWorkerRegistration>,
  onStage: (stage: string) => void,
): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }
  const registration = await register();
  onStage('worker-update');
  let failed = false;
  const observed = new Set<ServiceWorker>();
  const observe = () => {
    const worker = registration.installing;
    if (!worker || observed.has(worker)) return;
    observed.add(worker);
    worker.addEventListener('statechange', changed);
  };
  const changed = () => {
    if ([...observed].some((worker) => worker.state === 'redundant'))
      failed = true;
  };
  registration.addEventListener('updatefound', observe);
  observe();
  try {
    await withTimeout(
      registration.update(),
      20000,
      'Worker update check timed out',
    );
    observe();
    onStage('installation');
    const deadline = Date.now() + 60000;
    while (Date.now() < deadline) {
      if (
        navigator.serviceWorker.controller &&
        (await workerRelease(navigator.serviceWorker.controller))?.buildId ===
          release.buildId
      ) {
        window.location.reload();
        return;
      }
      const worker = registration.waiting;
      if (
        worker &&
        (await workerRelease(worker))?.buildId === release.buildId
      ) {
        onStage('activation');
        await new Promise<void>((resolve, reject) => {
          const done = () => {
            window.clearTimeout(timeout);
            navigator.serviceWorker.removeEventListener(
              'controllerchange',
              changed,
            );
          };
          const changed = async () => {
            const controller = navigator.serviceWorker.controller;
            if (
              controller &&
              (await workerRelease(controller))?.buildId === release.buildId
            ) {
              done();
              resolve();
            }
          };
          const timeout = window.setTimeout(() => {
            done();
            reject(
              new WebUpdateError('activation', 'Update activation timed out'),
            );
          }, 15000);
          navigator.serviceWorker.addEventListener('controllerchange', changed);
          try {
            worker.postMessage({
              type: 'ACTIVATE_UPDATE',
              buildId: release.buildId,
            });
          } catch (error) {
            done();
            reject(new WebUpdateError('activation', String(error)));
          }
        });
        window.location.reload();
        return;
      }
      if (failed || installationFailures.has(release.buildId))
        throw new WebUpdateError(
          'download',
          installationFailures.get(release.buildId) ||
            'Worker became redundant during installation',
        );
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    throw new WebUpdateError(
      'timeout',
      'No installed worker for the announced release within 60 seconds',
    );
  } finally {
    registration.removeEventListener('updatefound', observe);
    for (const worker of observed)
      worker.removeEventListener('statechange', changed);
  }
}
